import moment from "moment/moment";
import type {ActiveFilter} from "../components/common/filters/types";
import {formatDynamicDateLabel} from "./dynamic-date-utils";
import {formatBigMonetaryValue} from "../components/ideaNiv/Funcs";

const formatSelectedValues = (filter: any): string => {
  const { selected, type } = filter;
  if (selected == null) return '';

  if (type === 'date' || type === 'date-range') {
    if (Array.isArray(selected) && selected.length === 2) {
      const start = moment(selected[0]);
      const end = moment(selected[1]);
      if (start.isValid() && end.isValid()) {
        return `${start.format('MM/DD/YY')} – ${end.format('MM/DD/YY')}`;
      }
    }
    return String(selected);
  }

  if (type === 'number') {
    const op = filter.operator || '';
    return `${op} ${selected}`.trim();
  }

  if (Array.isArray(selected)) {
    if (selected.length === 0) return '';
    const first = String(selected[0]);
    if (selected.length === 1) return first;
    return `${first}, +${selected.length - 1}`;
  }

  return String(selected);
}

export const formatSelectedValuesWithLabels = (
  filter: any,
  optionLabelByValue: Map<string, string>
): string => {
  const { selected, type } = filter;
  if (selected == null) return '';

  if (type === 'date' || type === 'date-range' || type === 'number') {
    return formatSelectedValues(filter);
  }

  if (Array.isArray(selected)) {
    if (selected.length === 0) return '';
    const selectedFilterMap = (filter?.selectedFilterMap ?? {}) as Record<string, string>;
    const selectedFilterMapLower = new Map<string, string>(
      Object.entries(selectedFilterMap).map(([k, v]) => [String(k).toLowerCase(), String(v)])
    );
    const labels = selected.map((v: any) => {
      const raw = String(v);
      const key = raw.toLowerCase();
      return (
        selectedFilterMap[raw] ??
        selectedFilterMapLower.get(key) ??
        optionLabelByValue.get(key) ??
        raw
      );
    });
    if (labels.length === 1) return labels[0];
    return `${labels[0]}, +${labels.length - 1}`;
  }

  const raw = String(selected);
  const selectedKey = raw.toLowerCase();
  const selectedFilterMap = (filter?.selectedFilterMap ?? {}) as Record<string, string>;
  const selectedFilterMapLower = new Map<string, string>(
    Object.entries(selectedFilterMap).map(([k, v]) => [String(k).toLowerCase(), String(v)])
  );
  return (
    selectedFilterMap[raw] ??
    selectedFilterMapLower.get(selectedKey) ??
    optionLabelByValue.get(selectedKey) ??
    raw
  );
}

export const normalizeOptions = (options: any): { label: string; value: string }[] => {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options.map((opt: any) => ({
      label:
        opt?.label?.label ||
        opt?.label ||
        opt?.name ||
        opt?.value?.label ||
        String(opt?.value?.value ?? opt?._id ?? opt?.id ?? opt?.value ?? opt),
      value: String(
        opt?.value?.value ??
        opt?.value?._id ??
        opt?._id ??
        opt?.id ??
        opt?.value ??
        opt
      ).toLowerCase(),
    }));
  }

  if (typeof options === 'object') {
    return Object.entries(options).map(([key, val]: [string, any]) => ({
      label:
        typeof val === 'string' || typeof val === 'number'
          ? String(val)
          : val?.name || val?.label || key,
      value:
        typeof val === 'object' && val?.value !== undefined
          ? String(val.value).toLowerCase()
          : String(key).toLowerCase(),
    }));
  }

  return [];
}

function formatDateValue(value: string): string {
  const m = moment(value);
  return m.isValid() ? m.format('MM/DD/YYYY') : value;
}

/**
 * Format special checkbox filter values to user-friendly labels
 */
const formatCheckboxValue = (value: string): string => {
  const specialLabels: Record<string, string> = {
    unassigned: 'Unassigned',
    not_set: 'Not Set',
    set: 'Set',
  };
  return specialLabels[value] || value;
};

/**
 * Check if a filter is a change log filter based on dbFieldPath pattern
 */
export const isChangeLogFilter = (filter: any): boolean => {
  return filter?.dbFieldPath?.includes('.itemChangeLog[') &&
    filter?.operator?.match?.(/^\$(gt|gte|lt|lte):.+$/);
};

/**
 * Format change log filter for display
 */
export const formatChangeLogFilterLabel = (filter: any): string => {
  if (filter.label) return filter.label;

  const dbFieldPath = filter.dbFieldPath || '';
  const match = dbFieldPath.match(/\.itemChangeLog\[([^|]+)\|[^|]+\|([^\]]+)\]/);

  if (match) {
    const [, field, value] = match;
    const fieldParts = field.split('.');
    const fieldName = fieldParts[fieldParts.length - 1] === 'to' || fieldParts[fieldParts.length - 1] === 'from'
      ? fieldParts[fieldParts.length - 2]
      : fieldParts[fieldParts.length - 1];

    return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} → ${value}`;
  }

  return filter.name || filter.parent || 'Change Log Filter';
};

// English wording for number filter operators, keyed by both the long-form
// values stored in filter definitions and the abbreviated aliases the backend
// also accepts. The chip spells the operator out rather than echoing the
// definition's symbolic label, so it reads as a sentence.
const numberOperatorLabels: Record<string, string> = {
  equal: 'equals',
  eq: 'equals',
  not_equal: 'does not equal',
  neq: 'does not equal',
  greater_than: 'greater than',
  gt: 'greater than',
  lower_than: 'less than',
  lt: 'less than',
  gte: 'greater than or equal to',
  lte: 'less than or equal to',
};

export const getFilterTypeLabel = (filter: ActiveFilter): string => {
  // Handle change log filters
  if (isChangeLogFilter(filter)) {
    return 'in';
  }

  switch (filter.type) {
  case 'checkbox':
  case 'select':
  case 'fetchableCheckbox':
    return 'is in';
  case 'date':
  case 'date-range':
    return 'In between';
  case 'number': {
    const operator = filter.operator as string;
    if (!operator) return 'is';
    // For operators we have no wording for, the definition's own label still
    // beats the meaningless 'is'.
    const configuredLabel = filter.operations?.find(
      (operation) => String(operation.value) === String(operator)
    )?.label;
    return numberOperatorLabels[operator] || configuredLabel || 'is';
  }
  case 'boolean':
  default:
    // Handle userIdIn and other types not in type definition
    if ((filter.type as string) === 'userIdIn') return 'is';
    return 'is';
  }
}

// Build options map from filter options (traverses childComponents)
// Stores both exact key and lowercase key for flexible matching
const buildOptionsMap = (filterOptions: any[]): Record<string, string> => {
  const map: Record<string, string> = {};
  const traverse = (list: any[]) => {
    if (!Array.isArray(list)) return;
    list.forEach((o: any) => {
      if (!o) return;
      const label = o.label || o.name || '';
      const keys = [
        o.value,
        o._id,
        o.id,
        o.label,
        o.name,
        o?.value?.value,
        o?.value?._id,
      ]
        .filter((k) => k !== undefined && k !== null && String(k) !== '')
        .map((k) => String(k));
      // Store both exact and lowercase keys for flexible matching
      keys.forEach((exactKey) => {
        const lowerKey = exactKey.toLowerCase();
        if (exactKey) map[exactKey] = label;
        if (lowerKey && lowerKey !== exactKey) map[lowerKey] = label;
      });
      if (Array.isArray(o.childComponents) && o.childComponents.length > 0) {
        traverse(o.childComponents);
      }
    });
  };
  traverse(filterOptions);
  return map;
}

// Get options from filtersConfig for specific filters
const getFilterOptions = (filterName: string, filtersConfig: any): any[] => {
  if (!filtersConfig) return [];

  const aliases = Array.from(
    new Set([
      filterName,
      filterName?.replace(/^evidence-/, ''),
      filterName?.replace(/^idea-/, ''),
    ].filter(Boolean))
  );

  for (const key of aliases) {
    // Check idea section first, then evidence
    const ideaFilter = filtersConfig?.idea?.[key];
    if (ideaFilter?.options && Array.isArray(ideaFilter.options)) {
      return ideaFilter.options;
    }

    const evidenceFilter = filtersConfig?.evidence?.[key];
    if (evidenceFilter?.options && Array.isArray(evidenceFilter.options)) {
      return evidenceFilter.options;
    }
  }

  return [];
}

const isOwnerFilter = (filter: ActiveFilter): boolean => {
  const key = String(filter.name || filter.parent || '').toLowerCase();
  return key === 'owner' || key === 'evidence-owner' || key.includes('owner');
}

const mapOwnerMeFallback = (filter: ActiveFilter, raw: string, currentUserId?: string | null): string => {
  if (!currentUserId) return raw;
  if (!isOwnerFilter(filter)) return raw;
  return raw.toLowerCase() === currentUserId.toLowerCase() ? 'Me' : raw;
}

export const formatFilterValues = (filter: ActiveFilter, filtersConfig?: any, currentUserId?: string | null): string => {
  const { selected, options, query } = filter;

  // Handle change log filters specially
  if (isChangeLogFilter(filter)) {
    const safeSelected = Array.isArray(selected) ? selected : [];
    if (safeSelected.length > 0 && safeSelected[0]) {
      const dateRangeKey = safeSelected[0];
      // Check if it's a dynamic date key
      const dynamicLabel = formatDynamicDateLabel(dateRangeKey);
      if (dynamicLabel !== dateRangeKey) {
        return dynamicLabel;
      } else if (safeSelected.length === 2) {
        // Explicit date range
        return `${formatDateValue(safeSelected[0])} – ${formatDateValue(safeSelected[1])}`;
      }
    }
    return 'All Time';
  }

  if (filter.type === 'date' || filter.type === 'date-range') {
    // Check for dynamic date (savedKey or operator as string without selected array)
    const savedKey = (filter as any).savedKey || filter.operator;
    if (typeof savedKey === 'string' && (!Array.isArray(selected) || selected.length === 0)) {
      // Dynamic date - check if it's a known dynamic key
      const dynamicLabel = formatDynamicDateLabel(savedKey);
      if (dynamicLabel !== savedKey) {
        return dynamicLabel;
      }
    }

    // Handle different date filter formats:
    // 1. selected is an array [startDate, endDate] (evidence date filters)
    // 2. selected is an object { values: [startDate, endDate] } (idea date filters)
    // 3. query has { values: [startDate, endDate] }
    let values: [string, string] | undefined;

    if (Array.isArray(selected) && selected.length === 2) {
      // Format 1: selected is directly [startDate, endDate]
      values = selected as [string, string];
    } else if (selected && typeof selected === 'object' && 'values' in selected) {
      // Format 2: selected is { values: [startDate, endDate] }
      values = (selected as { values?: [string, string] }).values;
    } else if (query?.values) {
      // Format 3: query has the values
      values = query.values;
    }

    if (values?.length) {
      return values.map((v: string) => formatDateValue(v)).join(' – ');
    }
    return '—';
  }
  if (selected == null && query == null) return '—';
  if (filter.type === 'boolean') {
    // Check for operations in filterConfig to get proper label
    if (filtersConfig && filter.operator !== undefined) {
      const filterName = filter.name;
      const filterConfig = filtersConfig?.idea?.[filterName] || filtersConfig?.evidence?.[filterName];
      if (filterConfig?.operations) {
        const operation = filterConfig.operations.find((op: any) =>
          JSON.stringify(op.value) === JSON.stringify(filter.operator)
        );
        if (operation?.label) return operation.label;
      }
    }
    // Fallback to Yes/No based on operator value (since boolean stores value in operator)
    const boolValue = filter.operator ?? selected;
    return boolValue ? 'Yes' : 'No';
  }
  if ((filter.type as string) === 'userIdIn') {
    // Handle userIdIn type similar to boolean - check operations
    if (filtersConfig && filter.operator !== undefined) {
      const filterName = filter.name;
      const filterConfig = filtersConfig?.idea?.[filterName] || filtersConfig?.evidence?.[filterName];
      if (filterConfig?.operations) {
        const operation = filterConfig.operations.find((op: any) =>
          JSON.stringify(op.value) === JSON.stringify(filter.operator)
        );
        if (operation?.label) return operation.label;
      }
    }
    return String(filter.operator ?? '—');
  }
  if (filter.type === 'number') {
    // Format number values with formatBigMonetaryValue
    if (Array.isArray(selected) && selected.length > 0) {
      const numValue = Number(selected[0]);
      return !isNaN(numValue) ? (formatBigMonetaryValue(numValue, '') ?? String(numValue)) : String(selected[0]);
    }
    if (selected != null) {
      const numValue = Number(selected);
      return !isNaN(numValue) ? (formatBigMonetaryValue(numValue, '') ?? String(numValue)) : String(selected);
    }
    return '—';
  }
  if (Array.isArray(selected)) {
    if (!selected.length) return '—';
    const selectedFilterMap = (filter as any)?.selectedFilterMap as Record<string, string> | undefined;
    if (selectedFilterMap && Object.keys(selectedFilterMap).length > 0) {
      const selectedFilterMapLower = Object.fromEntries(
        Object.entries(selectedFilterMap).map(([k, v]) => [String(k).toLowerCase(), v])
      );
      return selected
        .map((s) => {
          const key = String(s);
          return (
            selectedFilterMap[key] ||
            selectedFilterMapLower[key.toLowerCase()] ||
            mapOwnerMeFallback(filter, key, currentUserId)
          );
        })
        .join(', ');
    }

    // Check if we need to look up options from filtersConfig
    // This handles filters with optionsKeyAppContext, dbFieldPath, or empty options
    const filterHasEmptyOptions = !options || (typeof options === 'object' && Object.keys(options).length === 0);
    const needsConfigLookup = filtersConfig && (
      filterHasEmptyOptions ||
      (filter as any).optionsKeyAppContext ||
      (filter as any).dbFieldPath
    );

    if (needsConfigLookup) {
      // Try to get options from filtersConfig using filter name or parent
      const filterName = filter.name || (filter as any).parent;
      const filterOptions = getFilterOptions(filterName, filtersConfig);
      if (filterOptions.length > 0) {
        const optionsMap = buildOptionsMap(filterOptions);
        const labels = selected.map((s) => {
          // Check if it's a special dynamic key first
          const specialLabel = formatCheckboxValue(String(s));
          if (specialLabel !== String(s)) return specialLabel;
          // Try both exact match and lowercase match
          return (
            optionsMap[String(s)] ||
            optionsMap[String(s).toLowerCase()] ||
            mapOwnerMeFallback(filter, String(s), currentUserId)
          );
        });
        return labels.join(', ');
      }
    }

    // Apply special checkbox value formatting for all array selections
    const optionsMap = Array.isArray(options) ? buildOptionsMap(options) : null;
    const labels =
      options && Object.keys(options).length > 0
        ? selected
          .map((id) => {
            const raw = String(id);
            const specialLabel = formatCheckboxValue(raw);
            if (specialLabel !== raw) return specialLabel;

            if (optionsMap) {
              return (
                optionsMap[raw] ||
                optionsMap[raw.toLowerCase()] ||
                mapOwnerMeFallback(filter, raw, currentUserId)
              );
            }

            return (
              options[raw] ??
              options[raw.toLowerCase?.() as any] ??
              mapOwnerMeFallback(filter, raw, currentUserId)
            );
          })
          .filter(Boolean)
        : selected.map((s) => {
          const specialLabel = formatCheckboxValue(String(s));
          if (specialLabel !== String(s)) return specialLabel;
          return mapOwnerMeFallback(filter, String(s), currentUserId);
        });
    return labels.join(', ');
  }
  if (options && selected != null) {
    return options[String(selected)] ?? mapOwnerMeFallback(filter, String(selected), currentUserId);
  }
  return String(selected ?? '—');
}